import {ContractDto} from '../../../options/contract.types'
import {OptionsDto} from '../../../options/types'

import {OrderChangeContractDto, OrderChangeOptionsDto} from './types'

/**
 * Maps Order Change API options to internal OrderProcessOptions format.
 *
 * This function transforms the order change API structure
 * into the format expected by the order process package.
 *
 * @param options - Raw API response for order change
 * @returns Mapped options in OrderProcessOptions format
 */
export const mapOrderChangeOptions = (options: OrderChangeOptionsDto): OptionsDto => {
  return {
    ...options,
    constraints: {
      ...options.constraints,
      poNumber: options.constraints.poNumber ?? {
        isEnabled: options.changeableFields?.customerReference?.isEnabled ?? false
      },
      notes: options.constraints.orderChangeNotes ?? {isEnabled: false}
    },
    contracts: mapContracts(options.contracts),
    workCalendar: {
      ...options.workCalendar,
      businessHours: {
        earliestPossibleLoadTime: options.workCalendar.businessHours.earliestPossible,
        latestPossibleLoadTime: options.workCalendar.businessHours.latestPossible,
        timeZone: options.workCalendar.businessHours.timezone
      },
      dateExceptions: {
        publicHolidays: options.workCalendar.publicHolidays?.map((date) => ({name: '', date})),
        excludedDates: options.workCalendar.excludedDays
      }
    }
  }
}

/**
 * Maps OrderChangeContractDto to ContractDto by converting contractValidityPeriods to dateRange
 * and normalizing materialsVariations (API naming) to materialVariations (internal naming).
 */
const mapContracts = (contracts: OrderChangeContractDto[]): ContractDto[] => {
  return contracts.map((contract) => {
    const {contractValidityPeriods, materials, ...rest} = contract
    return {
      ...rest,
      dateRange: {
        from: contractValidityPeriods.contractValidityStartDate,
        to: contractValidityPeriods.contractValidityEndDate
      },
      materials: materials.map(({materialsVariations, ...material}) => ({
        ...material,
        materialVariations: materialsVariations ?? null
      }))
    }
  })
}
